import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw, Radio, AlertTriangle, Clock, Globe } from "lucide-react";

const REFRESH_MS = 120000;
const ISO = {
  "Costa Rica": "cr", "Nicaragua": "ni", "Honduras": "hn", "El Salvador": "sv", "Panamá": "pa",
  "República Dominicana": "do", "Anguilla": "ai", "Quinielas y Primera": "do",
};
const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ""); return m ? +m[1] * 60 + +m[2] : -1; };

export const Flag = ({ country, className = "w-7 h-5" }) => ISO[country]
  ? <img src={`https://flagcdn.com/w80/${ISO[country]}.png`} alt={country} title={country} className={`${className} object-cover rounded-sm shadow-sm`} />
  : <span title={country} className={`${className} inline-flex items-center justify-center rounded-sm bg-slate-200 text-slate-600`}><Globe className="w-3.5 h-3.5" /></span>;

export const RecentDraws = ({ groups, tv = false }) => {
  const now = new Date().toLocaleTimeString("en-GB", { timeZone: "America/Costa_Rica", hour: "2-digit", minute: "2-digit" });
  const nowMin = toMin(now);
  const key = (d) => Math.min(toMin(d.time), nowMin);
  const recent = groups
    .flatMap((g) => g.draws.map((d) => ({ ...d, country: g.country })))
    .filter((d) => toMin(d.time) >= 0)
    .sort((a, b) => key(b) - key(a))
    .slice(0, 6);
  if (!recent.length) return null;
  return (
    <section className="rounded-2xl bg-slate-900 p-4 md:p-5 space-y-3" data-testid="live-recent">
      <h3 className={`${tv ? "text-3xl" : "text-base md:text-lg"} font-bold text-white`} style={{ fontFamily: "Space Grotesk, sans-serif" }}>🔥 Sorteos Recientes 🔥</h3>
      <div className={`grid gap-3 ${tv ? "grid-cols-6" : "grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"}`}>
        {recent.map((d, i) => (
          <div key={`${d.country}-${d.name}`} data-testid={`live-recent-${i}`}
            className="rounded-xl bg-white/5 border border-white/10 p-3 flex flex-col items-center text-center gap-2 transition-transform hover:-translate-y-0.5"
            style={{ animation: `fadeInUp 0.4s ease ${i * 60}ms both` }}>
            <div className={`${tv ? "w-32 h-32 text-6xl" : "w-16 h-16 md:w-20 md:h-20 text-2xl md:text-3xl"} rounded-full flex items-center justify-center bg-brand-gradient text-on-brand font-black font-mono shadow-brand ring-4 ring-white/10`}>
              {d.numbers[0]}
            </div>
            {d.numbers.length > 1 && (
              <div className="flex flex-wrap justify-center gap-1 max-w-full">
                {d.numbers.slice(1, 7).map((n, j) => (
                  <span key={j} className={`${tv ? "min-w-[44px] h-[44px] text-xl" : "min-w-[26px] h-[26px] text-xs"} px-1 rounded-full flex items-center justify-center bg-white/15 text-white font-bold font-mono`}>{n}</span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1.5 min-w-0 max-w-full">
              <Flag country={d.country} className={`${tv ? "w-8 h-6" : "w-5 h-3.5"} shrink-0`} />
              <span className={`${tv ? "text-xl" : "text-xs"} font-semibold text-white line-clamp-2 leading-tight`} title={d.name}>{d.name}</span>
            </div>
            <span className={`flex items-center gap-1 ${tv ? "text-xl" : "text-xs"} font-mono text-brand`}><Clock className={tv ? "w-5 h-5" : "w-3 h-3"} />{d.time}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

const DrawCard = ({ d, latest }) => (
  <div
    className={`rounded-xl border bg-white p-3 flex flex-col gap-2 transition-shadow hover:shadow-md ${latest ? "border-brand ring-1 ring-brand" : "border-slate-200"}`}
    data-testid={`live-draw-${slug(d.name)}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-semibold text-sm text-slate-800 leading-tight truncate" title={d.name}>{d.name}</p>
        {d.company && <p className="text-[11px] text-slate-500">{d.company}</p>}
      </div>
      <span className="flex items-center gap-1 text-xs font-mono font-semibold text-slate-600 shrink-0">
        <Clock className="w-3 h-3" />{d.time || "—"}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {d.numbers.map((n, i) => (
        <span key={i} className={`min-w-[34px] h-[34px] px-1 rounded-full flex items-center justify-center font-bold font-mono text-sm ${i === 0 ? "bg-slate-900 text-white" : "bg-brand-soft text-slate-900 border border-brand"}`}>
          {n}
        </span>
      ))}
      {d.extra?.map((x, i) => (
        <span key={`x${i}`} className="h-[34px] px-2 rounded-full flex items-center justify-center font-bold text-xs bg-red-100 text-red-700 border border-red-300">{x}</span>
      ))}
    </div>
    {latest && <span className="self-start text-[10px] font-bold uppercase tracking-wide text-brand">Último sorteo</span>}
  </div>
);

export const LiveDrawResults = ({ fetcher, headerExtra, publicMode = false }) => {
  const [day, setDay] = useState("hoy");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("all");
  const [nextIn, setNextIn] = useState(REFRESH_MS / 1000);
  const timerRef = useRef(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = fetcher
        ? await fetcher(day)
        : await axiosInstance.get(`/lottery/live-results?day=${day}${refresh ? "&refresh=true" : ""}`);
      setData(r.data);
    } catch (_e) { /* keep last data */ }
    setLoading(false);
    setNextIn(REFRESH_MS / 1000);
  }, [day, fetcher]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => {
      setNextIn((s) => {
        if (s <= 1) { if (!document.hidden) load(); return REFRESH_MS / 1000; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const groups = data?.groups || [];
  const shown = country === "all" ? groups : groups.filter((g) => g.country === country);
  const failed = (data?.sources || []).filter((s) => !s.ok);
  const updated = data?.updated_at ? new Date(data.updated_at).toLocaleTimeString("es-CR", { timeZone: "America/Costa_Rica", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="space-y-4" data-testid="live-results">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            <Radio className={`w-5 h-5 ${day === "hoy" ? "text-red-500 animate-pulse" : "text-slate-400"}`} /> Resultados de Sorteos
          </h2>
          <p className="text-xs text-slate-500" data-testid="live-results-updated">
            {data?.date} · Actualizado {updated} · próxima en {Math.floor(nextIn / 60)}:{String(nextIn % 60).padStart(2, "0")} · horas en hora de Costa Rica
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
            {["hoy", "ayer"].map((d) => (
              <button key={d} onClick={() => setDay(d)} data-testid={`live-day-${d}`}
                className={`px-4 h-9 rounded-md text-sm font-medium transition-colors ${day === d ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-white"}`}>
                {d === "hoy" ? "Hoy" : "Ayer"}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="h-10 gap-1" onClick={() => load(true)} disabled={loading} data-testid="live-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
      </div>

      {headerExtra}

      {groups.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="live-country-filter">
          {[{ country: "all" }, ...groups].map((g) => (
            <button key={g.country} onClick={() => setCountry(g.country)} data-testid={`live-country-${slug(g.country)}`}
              className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${country === g.country ? "bg-brand border-brand text-on-brand" : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"}`}>
              {g.country === "all" ? "Todos" : `${g.country} (${g.draws.length})`}
            </button>
          ))}
        </div>
      )}

      {failed.length > 0 && !publicMode && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="live-source-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" /> No se pudo consultar: {failed.map((s) => s.name).join(", ")}. Se reintentará automáticamente.
        </div>
      )}

      {!data && loading && <p className="text-sm text-slate-500 p-6 text-center">Consultando resultados…</p>}
      {data && groups.length === 0 && <Card><CardContent className="p-6 text-center text-slate-500">Aún no hay resultados publicados para este día.</CardContent></Card>}

      <RecentDraws groups={groups} />

      {shown.map((g) => (
        <section key={g.country} className="space-y-2" data-testid={`live-group-${slug(g.country)}`}>
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
            <Flag country={g.country} />{g.country}
            <span className="text-xs font-medium text-slate-400 normal-case">{g.draws.length} sorteos</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {g.draws.map((d, i) => <DrawCard key={`${d.name}-${i}`} d={d} latest={day === "hoy" && i === g.draws.length - 1} />)}
          </div>
        </section>
      ))}
      {data && !publicMode && <p className="text-[11px] text-slate-400">Fuentes: loteriascostarica.com, enloteria.com, loteriasdominicanas.com. Verifica siempre con la fuente oficial antes de pagar premios.</p>}
    </div>
  );
};
